import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import BackButton from "../../partials/CommonPartials/BackButton";
import {
  validateEmail,
  validatePassword,
} from "../../../utils/validators/auth.validator";
import { useSignin } from "../../../hooks/authHooks/useSignin";
import type { ISignInCredentials } from "../../../interfaces/auth.interface";
import Spinner from "../../loaders/Spinner";
import EmailInput from "../../partials/AuthPartials/EmailInput";
import PasswordInput from "../../partials/AuthPartials/PasswordInput";
import TurnstileWidget from "../../partials/CommonPartials/TurnstileWidget";

const SignIn = () => {
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const [createFormData, setCreateFormData] = useState<ISignInCredentials>({
    email: "",
    password: "",
  });

  const [formErrors, setFormErrors] = useState<
    Partial<Record<keyof ISignInCredentials, string>>
  >({});

  const { mutate: signinMutate, isPending: isSigningIn } = useSignin();

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken("");
  }, []);

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name as keyof ISignInCredentials;
    const value = e.target.value;

    setCreateFormData({ ...createFormData, [name]: value });
    setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    const name = e.target.name as keyof ISignInCredentials;
    const value = e.target.value;
    let error: string | undefined;

    if (name === "email") {
      error = validateEmail(value);
    }
    if (name === "password") {
      error = validatePassword(value);
    }

    setFormErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const emailError = validateEmail(createFormData.email);
    const passwordError = validatePassword(createFormData.password);

    if (emailError || passwordError) {
      setFormErrors({
        email: emailError,
        password: passwordError,
      });
      return;
    }

    signinMutate({ ...createFormData, turnstileToken });
  };

  return (
    <div className="w-full min-h-screen pt-45 max-xs:pt-35">
      <title>Sign In | MonoShare</title>
      <meta name="robots" content="noindex, follow" />
      <div className="max-xs:w-full max-xs:rounded-none max-xs:border-l-0 max-xs:border-r-0 max-xs:px-4 relative flex flex-col w-md h-fit rounded-xl m-auto py-8 px-8 z-10 bg-(--white)/3 border-gray-400/20 border">
        <div className="absolute -top-15 left-0 opacity-70 hover:opacity-100">
          <BackButton />
        </div>
        <p className="mx-auto text-3xl max-xs:text-2xl">Welcome</p>
        <h1 className="text-2xl max-xs:text-xl noto-sans mt-7 mb-1">Sign In</h1>
        <p className="text-sm text-(--gray) noto-sans">
          Enter your account to access all features.
        </p>
        <form onSubmit={submitForm}>
          {/* Email */}
          <EmailInput
            email={createFormData.email}
            error={formErrors.email}
            onChange={onChangeHandler}
            onBlur={onBlurHandler}
            onFocus={() =>
              setFormErrors((prev) => ({
                ...prev,
                email: undefined,
              }))
            }
          />
          {/* Password */}
          <PasswordInput
            type="Password"
            value={createFormData.password}
            error={formErrors.password}
            onChange={onChangeHandler}
            onBlur={onBlurHandler}
            onFocus={() =>
              setFormErrors((prev) => ({
                ...prev,
                password: undefined,
              }))
            }
          />

          {/* Forgot password */}
          <div className="flex justify-end">
            <Link
              to="/forgot-password"
              className="noto-sans text-xs duration-0 text-(--gray) hover:text-(--white) transition-colors hover:underline underline-offset-2"
            >
              Forgot password?
            </Link>
          </div>

          {/* Cloudflare Captcha  */}
          <TurnstileWidget
            onVerify={setTurnstileToken}
            onExpire={handleTurnstileExpire}
            containerClasses="my-2 overflow-hidden rounded-lg"
          />

          {/* submit button */}
          <button
            disabled={isSigningIn}
            className={`${formErrors.email || formErrors.password ? "bg-red-400/70 text-(--white)/80" : "bg-(--white) text-black"} flex items-center justify-center mt-2 noto-sans w-full h-10.5 text-sm font-medium cursor-pointer rounded-lg transition-colors duration-300 ease-in-out`}
          >
            {isSigningIn ? (
              <Spinner size="size-4.5" thickness="border-2" clr="text-black" />
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        {/* Create account cta */}
        <div className="m-auto mt-5">
          <p className="noto-sans text-sm font-normal text-(--gray)">
            New to MonoShare?{" "}
            <Link
              to="/create-account"
              className="text-(--main-light-blue) font-normal hover:underline"
            >
              Create account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
